/**
 * Collection Filtering, Sorting, and Infinite Scroll
 *
 * Haalt volgende pagina's pas op wanneer de bezoeker in de buurt komt, met één
 * pagina als buffer. Daarvoor werd bij het laden van elke collectiepagina
 * meteen álles opgehaald: op /collections/alle-kamerplanten waren dat 22
 * pagina's van 671 KB, samen bijna 15 MB HTML die ook nog stuk voor stuk als
 * volledig document geparst werd. Dat was met 1.358 ms het zwaarste script op
 * de pagina.
 *
 * Twee wijzigingen:
 *  - lui laden in plaats van alles vooraf, met een buffer van één pagina zodat
 *    de eerste scroll nog steeds direct reageert;
 *  - ophalen via ?section_id=, zodat alleen de collectiesectie terugkomt
 *    (313 KB in plaats van 671 KB) en er geen compleet document geparst wordt.
 */
document.addEventListener('DOMContentLoaded', function() {
    const filterForm = document.querySelector('#filters');
    const sortSelects = document.querySelectorAll('.js-sort-by');
    const productGrid = document.querySelector('#products-grid');
    const infiniteScrollTrigger = document.querySelector('#infinite-scroll-trigger');
    const loadingIndicator = document.querySelector('#infinite-scroll-loading');
    const endMessage = document.querySelector('#infinite-scroll-end');

    // Exit early if required elements are not found
    if (!filterForm || sortSelects.length === 0 || !productGrid) {
        return;
    }

    // Initialize Shopify query params object if not exists
    if (typeof Shopify === 'undefined') {
        window.Shopify = {};
    }
    Shopify.queryParams = {};

    // Preload state
    let currentPage = infiniteScrollTrigger ? parseInt(infiniteScrollTrigger.dataset.currentPage) : 1;
    let totalPages = infiniteScrollTrigger ? parseInt(infiniteScrollTrigger.dataset.totalPages) : 1;
    let preloadedProducts = [];
    let isPreloading = false;
    let preloadComplete = false;
    let revealObserver = null;
    // Eerstvolgende pagina die nog opgehaald moet worden.
    let nextPage = currentPage + 1;

    // Hoeveel kaarten er per keer zichtbaar worden, en de ondergrens waaronder
    // we alvast de volgende pagina ophalen.
    const REVEAL_BATCH = 24;
    // De sectie die de productgrid rendert; hiermee vraagt Shopify alleen dat
    // stuk op in plaats van de hele pagina.
    const COLLECTION_SECTION_ID = 'single-collection';

    /**
     * Get query parameters from the current URL
     */
    const getQueryParams = () => {
        const queryParams = {};
        if (location.search.length) {
            const params = new URLSearchParams(location.search);
            params.forEach((value, key) => {
                if (queryParams[key]) {
                    queryParams[key] = [].concat(queryParams[key], value);
                } else {
                    queryParams[key] = value;
                }
            });
        }
        return queryParams;
    };

    Shopify.queryParams = getQueryParams();

    /**
     * Build URL with current filters, sorting, and page number
     */
    const buildUrlWithParams = (pageNum) => {
        const queryParams = { ...Shopify.queryParams };
        queryParams.page = pageNum;

        const searchParams = new URLSearchParams();
        Object.keys(queryParams).forEach(key => {
            if (Array.isArray(queryParams[key])) {
                queryParams[key].forEach(value => searchParams.append(key, value));
            } else {
                searchParams.append(key, queryParams[key]);
            }
        });

        return `${window.location.pathname}?${searchParams.toString()}`;
    };

    /**
     * Preload all remaining pages in the background
     */
    const preloadNextPage = async () => {
        if (isPreloading) return;

        if (nextPage > totalPages) {
            preloadComplete = true;
            if (loadingIndicator) loadingIndicator.classList.add('d-none');
            return;
        }

        isPreloading = true;
        if (loadingIndicator) loadingIndicator.classList.remove('d-none');

        try {
            const url = buildUrlWithParams(nextPage) + '&section_id=' + COLLECTION_SECTION_ID;
            const response = await axios.get(url);
            const parser = new DOMParser();
            const doc = parser.parseFromString(response.data, 'text/html');

            const newProductsContainer = doc.querySelector('#products-grid');
            if (newProductsContainer) {
                const newProducts = newProductsContainer.querySelectorAll(':scope > div');
                newProducts.forEach(product => {
                    preloadedProducts.push(product.cloneNode(true));
                });
            }
            nextPage++;
        } catch (error) {
            console.error('Error preloading products:', error);
            // Ook bij een fout doortellen, anders blijft dezelfde pagina
            // eindeloos opnieuw geprobeerd worden.
            nextPage++;
        }

        isPreloading = false;
        if (nextPage > totalPages) {
            preloadComplete = true;
        }
        if (loadingIndicator) loadingIndicator.classList.add('d-none');

        // Stond de trigger al in beeld toen dit begon, dan komt er geen nieuwe
        // intersectie-melding meer; dan tonen we hier zelf. Dit stopt vanzelf
        // zodra er genoeg kaarten staan om de trigger uit beeld te duwen.
        if (preloadedProducts.length > 0 && triggerIsNear()) {
            revealMoreProducts();
        }
    };

    /**
     * Trustpilot-widgets in nieuw toegevoegde kaarten alsnog laten laden.
     *
     * tp.widget.bootstrap.min.js scant de pagina één keer bij het laden. Alles
     * wat daarna in de DOM komt blijft onaangeraakt staan: je ziet dan alleen
     * de terugvaltekst "Trustpilot" in plaats van de sterrenbalk. Bij het
     * doorscrollen van een collectie gebeurde dat vanaf de tweede lading.
     *
     * Dit stond ook al zo op het live thema - het is geen gevolg van de
     * omschakeling naar lui laden, maar het valt nu wel netjes op te lossen op
     * het moment dat we zelf kaarten toevoegen.
     */
    const activeerTrustpilot = (elementen) => {
        if (!window.Trustpilot || typeof window.Trustpilot.loadFromElement !== 'function') return;

        elementen.forEach(el => {
            if (!el || typeof el.querySelectorAll !== 'function') return;

            const widgets = el.matches && el.matches('.trustpilot-widget')
                ? [el]
                : Array.from(el.querySelectorAll('.trustpilot-widget'));

            widgets.forEach(widget => {
                // Al geïnitialiseerd? Dan heeft hij een iframe en laten we hem staan.
                if (widget.querySelector('iframe')) return;
                try {
                    window.Trustpilot.loadFromElement(widget, true);
                } catch (e) {
                    // Widget niet kunnen laden is vervelend maar mag de rij niet breken.
                }
            });
        });
    };

    /**
     * Staat de scroll-trigger nu binnen (of vlak onder) het scherm?
     *
     * De IntersectionObserver meldt alleen een *verandering*. Na filteren of
     * sorteren staat de trigger al in beeld en vuurt hij dus één keer af —
     * precies terwijl de eerste pagina nog opgehaald wordt, waardoor die ene
     * melding verloren ging en er daarna nooit meer iets bijkwam. Daarom kijken
     * we na het ophalen zelf even waar de trigger staat.
     */
    const triggerIsNear = () => {
        if (!infiniteScrollTrigger) return false;
        const rect = infiniteScrollTrigger.getBoundingClientRect();
        return rect.top < window.innerHeight + 800;
    };

    /**
     * Zorgt dat er alvast één pagina klaarstaat, zodat de eerstvolgende scroll
     * niet op het netwerk hoeft te wachten.
     */
    const topUpBuffer = () => {
        if (!preloadComplete && !isPreloading && preloadedProducts.length < REVEAL_BATCH) {
            preloadNextPage();
        }
    };

    /**
     * Reveal next batch of preloaded products
     */
    const revealMoreProducts = async () => {
        // Buffer leeg maar er zijn nog pagina's: eerst ophalen, dan tonen.
        if (preloadedProducts.length === 0 && !preloadComplete) {
            await preloadNextPage();
        }

        const productsToAdd = preloadedProducts.splice(0, REVEAL_BATCH);

        if (productsToAdd.length === 0) {
            if (preloadComplete && preloadedProducts.length === 0) {
                if (endMessage) endMessage.classList.remove('d-none');
                if (revealObserver) revealObserver.disconnect();
            }
            return;
        }

        productsToAdd.forEach(product => {
            productGrid.appendChild(product);
        });

        activeerTrustpilot(productsToAdd);

        // Check if all done
        if (preloadComplete && preloadedProducts.length === 0) {
            if (endMessage) endMessage.classList.remove('d-none');
            if (revealObserver) revealObserver.disconnect();
        } else {
            // Vast de volgende pagina klaarzetten voor de scroll hierna.
            topUpBuffer();
        }
    };

    /**
     * Initialize scroll-based reveal
     */
    const initRevealOnScroll = () => {
        if (!infiniteScrollTrigger) return;

        if (totalPages <= 1) {
            if (endMessage) endMessage.classList.remove('d-none');
            return;
        }

        if (revealObserver) {
            revealObserver.disconnect();
        }

        revealObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                // Geen check meer op een gevulde buffer: revealMoreProducts
                // haalt zelf op wanneer er niets klaarstaat. isPreloading
                // voorkomt dat een tweede intersectie dezelfde fetch start.
                if (entry.isIntersecting && !isPreloading) {
                    revealMoreProducts();
                }
            });
        }, {
            root: null,
            rootMargin: '800px',
            threshold: 0
        });

        revealObserver.observe(infiniteScrollTrigger);
    };

    /**
     * Reset state and preload products (used when filtering/sorting)
     */
    const resetAndPreload = (doc) => {
        const newTrigger = doc.querySelector('#infinite-scroll-trigger');
        if (newTrigger) {
            currentPage = parseInt(newTrigger.dataset.currentPage);
            totalPages = parseInt(newTrigger.dataset.totalPages);
        }

        preloadedProducts = [];
        isPreloading = false;
        preloadComplete = false;
        // Na filteren/sorteren begint de paginering opnieuw.
        nextPage = currentPage + 1;

        if (loadingIndicator) loadingIndicator.classList.add('d-none');

        if (endMessage) {
            if (totalPages <= 1) {
                endMessage.classList.remove('d-none');
            } else {
                endMessage.classList.add('d-none');
            }
        }

        initRevealOnScroll();
        topUpBuffer();
    };

    /**
     * Fetch ALL product IDs from a collection using Shopify JSON API
     * /collections/{handle}/products.json returns up to 250 per page
     */
    const collectionIdCache = {};
    const fetchCollectionProductIds = async (collectionHandle) => {
        // Return cached result if available
        if (collectionIdCache[collectionHandle]) return collectionIdCache[collectionHandle];

        const ids = new Set();
        let page = 1;

        while (true) {
            const rootPath = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
            const response = await axios.get(`${rootPath}collections/${collectionHandle}/products.json?limit=250&page=${page}`);
            const products = response.data.products;
            if (!products || products.length === 0) break;
            products.forEach(p => ids.add(String(p.id)));
            if (products.length < 250) break;
            page++;
        }

        collectionIdCache[collectionHandle] = ids;
        return ids;
    };

    /**
     * Fetch all pages of a collection's HTML and return matching product cards
     * Fetches page 1 first to get totalPages, then remaining pages in parallel
     */
    const fetchFilteredCards = async (collectionPath, targetIds) => {
        const matchingCards = [];

        // Fetch page 1 to get totalPages
        const separator = collectionPath.includes('?') ? '&' : '?';
        const firstResponse = await axios.get(`${collectionPath}${separator}page=1`);
        const firstDoc = new DOMParser().parseFromString(firstResponse.data, 'text/html');
        const firstGrid = firstDoc.querySelector('#products-grid');
        if (!firstGrid) return matchingCards;

        const trigger = firstDoc.querySelector('#infinite-scroll-trigger');
        const maxPages = trigger ? parseInt(trigger.dataset.totalPages) : 1;

        // Collect matches from page 1
        firstGrid.querySelectorAll(':scope > div').forEach(card => {
            const block = card.querySelector('[data-product-id]');
            if (block && targetIds.has(block.getAttribute('data-product-id'))) {
                matchingCards.push(card.cloneNode(true));
            }
        });

        // Fetch remaining pages in parallel
        if (maxPages > 1) {
            const pagePromises = [];
            for (let p = 2; p <= maxPages; p++) {
                pagePromises.push(axios.get(`${collectionPath}${collectionPath.includes('?') ? '&' : '?'}page=${p}`));
            }
            const responses = await Promise.all(pagePromises);
            responses.forEach(response => {
                const doc = new DOMParser().parseFromString(response.data, 'text/html');
                const grid = doc.querySelector('#products-grid');
                if (grid) {
                    grid.querySelectorAll(':scope > div').forEach(card => {
                        const block = card.querySelector('[data-product-id]');
                        if (block && targetIds.has(block.getAttribute('data-product-id'))) {
                            matchingCards.push(card.cloneNode(true));
                        }
                    });
                }
            });
        }

        return matchingCards;
    };

    // Track active collection pill for intersection filtering
    let activeCollectionPill = null;

    /**
     * Update query parameters and reload filtered results
     */
    const updateParamsAndFilter = (resetPage = true) => {
        const queryParams = getQueryParams();
        const formData = new FormData(filterForm);

        Array.from(filterForm.elements).forEach(element => {
            if (element.name && queryParams[element.name]) {
                delete queryParams[element.name];
            }
        });

        formData.forEach((value, key) => {
            if (value) {
                if (queryParams[key]) {
                    queryParams[key] = [].concat(queryParams[key], value);
                } else {
                    queryParams[key] = value;
                }
            }
        });

        if (resetPage) {
            delete queryParams.page;
        }

        if (Shopify.queryParams.sort_by) {
            queryParams.sort_by = Shopify.queryParams.sort_by;
        }

        const searchParams = new URLSearchParams();
        Object.keys(queryParams).forEach(key => {
            if (Array.isArray(queryParams[key])) {
                queryParams[key].forEach(value => searchParams.append(key, value));
            } else {
                searchParams.append(key, queryParams[key]);
            }
        });

        const urlWithParams = `${window.location.pathname}?${searchParams.toString()}`;

        window.history.pushState({}, '', urlWithParams);
        Shopify.queryParams = queryParams;

        // If a collection pill is active, apply intersection filtering
        if (activeCollectionPill) {
            const filteredUrl = `${originalCollectionPath}?${searchParams.toString()}`;
            fetchCollectionProductIds(activeCollectionPill).then(async (targetIds) => {
                const matchingCards = await fetchFilteredCards(filteredUrl, targetIds);

                productGrid.innerHTML = '';
                matchingCards.forEach(card => productGrid.appendChild(card));

                const productCount = document.querySelector('.collection-product-count');
                if (productCount) {
                    const count = matchingCards.length;
                    const countNumbers = productCount.querySelectorAll('.js-product-count-number');
                    if (countNumbers.length) {
                        countNumbers.forEach(el => el.textContent = count);
                    } else {
                        productCount.textContent = count + (count === 1 ? ' product' : ' producten');
                    }
                }

                if (revealObserver) revealObserver.disconnect();
                preloadedProducts = [];
                preloadComplete = true;
                isPreloading = false;
                if (endMessage) endMessage.classList.remove('d-none');
                if (loadingIndicator) loadingIndicator.classList.add('d-none');
            }).catch(error => console.error('Error loading filtered products:', error));
        } else {
            axios.get(urlWithParams)
                .then(response => {
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(response.data, 'text/html');

                    productGrid.innerHTML = doc.querySelector('#products-grid').innerHTML;

                    const resultsDiv = document.querySelector('#results');
                    const newResultsDiv = doc.querySelector('#results');
                    if (resultsDiv && newResultsDiv) {
                        resultsDiv.innerHTML = newResultsDiv.innerHTML;
                    }

                    const resultsDivMobile = document.querySelector('#results-mobile');
                    const newResultsDivMobile = doc.querySelector('#results-mobile');
                    if (resultsDivMobile && newResultsDivMobile) {
                        resultsDivMobile.innerHTML = newResultsDivMobile.innerHTML;
                    }

                    // Update product count in collection header
                    const productCount = document.querySelector('.collection-product-count');
                    const newProductCount = doc.querySelector('.collection-product-count');
                    if (productCount && newProductCount) {
                        productCount.innerHTML = newProductCount.innerHTML;
                    }

                    // Do NOT replace filter form content — filters are applied via "Apply" button
                    // and should remain as-is so users can adjust and re-apply.

                    resetAndPreload(doc);
                })
                .catch(error => console.error('Error loading filtered products:', error));
        }
    };

    /**
     * Handle sort change
     */
    const handleSortChange = (sortValue) => {
        Shopify.queryParams.sort_by = sortValue;

        const queryParams = getQueryParams();
        queryParams.sort_by = sortValue;
        delete queryParams.page;

        Shopify.queryParams = queryParams;

        const searchParams = new URLSearchParams();
        Object.keys(queryParams).forEach(key => {
            const val = queryParams[key];
            if (Array.isArray(val)) {
                val.forEach(v => searchParams.append(key, v));
            } else {
                searchParams.append(key, String(val));
            }
        });

        const urlWithParams = `${window.location.pathname}?${searchParams.toString()}`;

        window.history.pushState({}, '', urlWithParams);

        if (activeCollectionPill) {
            fetchCollectionProductIds(activeCollectionPill).then(async (targetIds) => {
                const matchingCards = await fetchFilteredCards(urlWithParams, targetIds);

                productGrid.innerHTML = '';
                matchingCards.forEach(card => productGrid.appendChild(card));

                const productCount = document.querySelector('.collection-product-count');
                if (productCount) {
                    const count = matchingCards.length;
                    const countNumbers = productCount.querySelectorAll('.js-product-count-number');
                    if (countNumbers.length) {
                        countNumbers.forEach(el => el.textContent = count);
                    } else {
                        productCount.textContent = count + (count === 1 ? ' product' : ' producten');
                    }
                }

                if (revealObserver) revealObserver.disconnect();
                preloadedProducts = [];
                preloadComplete = true;
                isPreloading = false;
                if (endMessage) endMessage.classList.remove('d-none');
                if (loadingIndicator) loadingIndicator.classList.add('d-none');
            }).catch(error => console.error('Error loading sorted products:', error));
        } else {
            axios.get(urlWithParams)
                .then(response => {
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(response.data, 'text/html');

                    productGrid.innerHTML = doc.querySelector('#products-grid').innerHTML;

                    const resultsDiv = document.querySelector('#results');
                    const newResultsDiv = doc.querySelector('#results');
                    if (resultsDiv && newResultsDiv) {
                        resultsDiv.innerHTML = newResultsDiv.innerHTML;
                    }

                    const resultsDivMobile = document.querySelector('#results-mobile');
                    const newResultsDivMobile = doc.querySelector('#results-mobile');
                    if (resultsDivMobile && newResultsDivMobile) {
                        resultsDivMobile.innerHTML = newResultsDivMobile.innerHTML;
                    }

                    // Update product count in collection header
                    const productCount = document.querySelector('.collection-product-count');
                    const newProductCount = doc.querySelector('.collection-product-count');
                    if (productCount && newProductCount) {
                        productCount.innerHTML = newProductCount.innerHTML;
                    }

                    resetAndPreload(doc);
                })
                .catch(error => console.error('Error loading sorted products:', error));
        }
    };

    // Initialize
    initRevealOnScroll();

    // De eerste buffer pas vullen wanneer de browser niets dringenders te doen
    // heeft. Meteen beginnen kostte ruim een seconde op de hoofdthread, precies
    // in het venster waarin de pagina zichtbaar en klikbaar moet worden.
    const warmUpBuffer = () => topUpBuffer();
    if ('requestIdleCallback' in window) {
        requestIdleCallback(warmUpBuffer, { timeout: 3000 });
    } else {
        setTimeout(warmUpBuffer, 1500);
    }

    // Event listeners

    // Quick filter pills and collection pills — single-select across both types
    const quickFilterPills = document.querySelectorAll('.js-quick-filter');
    const collectionPills = document.querySelectorAll('.js-collection-pill');
    const originalCollectionPath = window.location.pathname;

    /**
     * Deactivate all pills (both quick filters and collection pills)
     */
    const deactivateAllPills = () => {
        quickFilterPills.forEach(p => p.classList.remove('is-active'));
        collectionPills.forEach(p => p.classList.remove('is-active'));
        activeCollectionPill = null;
    };

    /**
     * Fetch URL via AJAX and update product grid
     */
    const fetchAndUpdateGrid = (url) => {
        window.history.pushState({}, '', url);
        Shopify.queryParams = getQueryParams();

        axios.get(url)
            .then(response => {
                const parser = new DOMParser();
                const doc = parser.parseFromString(response.data, 'text/html');

                productGrid.innerHTML = doc.querySelector('#products-grid').innerHTML;

                const productCount = document.querySelector('.collection-product-count');
                const newProductCount = doc.querySelector('.collection-product-count');
                if (productCount && newProductCount) {
                    productCount.innerHTML = newProductCount.innerHTML;
                }

                resetAndPreload(doc);

                // Scroll to top after filter is applied
                window.scrollTo({ top: 0, behavior: 'smooth' });
            })
            .catch(error => console.error('Error applying filter:', error));
    };

    // Quick filter pills — metafield-based filters
    quickFilterPills.forEach(pill => {
        pill.addEventListener('click', function(e) {
            e.preventDefault();
            const param = this.dataset.param;
            const value = this.dataset.value;
            const isActive = this.classList.contains('is-active');

            // Deactivate ALL pills (both types)
            deactivateAllPills();

            if (isActive) {
                // Deselecting — go back to original collection with no filters
                fetchAndUpdateGrid(originalCollectionPath);
            } else {
                // Selecting — clean URL with only this filter on original collection
                this.classList.add('is-active');
                const params = new URLSearchParams();
                params.set(param, value);
                fetchAndUpdateGrid(originalCollectionPath + '?' + params.toString());
            }
        });
    });

    // Collection pills — intersection: show current collection products that also exist in target collection
    collectionPills.forEach(pill => {
        pill.addEventListener('click', async function(e) {
            e.preventDefault();
            const targetHandle = this.dataset.collectionHandle;
            const targetUrl = this.dataset.collectionUrl;
            const isActive = this.classList.contains('is-active');

            // Deactivate ALL pills (both types)
            deactivateAllPills();

            if (isActive) {
                // Deselecting — go back to original collection
                activeCollectionPill = null;
                fetchAndUpdateGrid(originalCollectionPath);
            } else {
                this.classList.add('is-active');
                activeCollectionPill = targetHandle;

                // Stop infinite scroll while we work
                if (revealObserver) revealObserver.disconnect();
                preloadedProducts = [];
                preloadComplete = true;
                isPreloading = false;

                // Fetch target IDs using default-language handle (JSON API always uses default handles)
                const targetIds = await fetchCollectionProductIds(targetHandle);
                const matchingCards = await fetchFilteredCards(originalCollectionPath, targetIds);

                // Replace grid with only matching products
                productGrid.innerHTML = '';
                matchingCards.forEach(card => productGrid.appendChild(card));

                // Update count
                const productCount = document.querySelector('.collection-product-count');
                if (productCount) {
                    const count = matchingCards.length;
                    const countNumbers = productCount.querySelectorAll('.js-product-count-number');
                    if (countNumbers.length) {
                        countNumbers.forEach(el => el.textContent = count);
                    } else {
                        productCount.textContent = count + (count === 1 ? ' product' : ' producten');
                    }
                }

                if (endMessage) endMessage.classList.remove('d-none');
                if (loadingIndicator) loadingIndicator.classList.add('d-none');

                // Scroll to top after filter is applied
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        });
    });

    // Filters are NOT applied on change — only when "Apply" button is clicked
    const applyFilterBtns = document.querySelectorAll('.js-filter-apply');
    applyFilterBtns.forEach(btn => {
        btn.addEventListener('click', function(e) {
            e.preventDefault();
            updateParamsAndFilter(true);
            // Close the filter drawer by clicking a close button (triggers closeFilterPanel + unlockHTML)
            const closeBtn = document.querySelector('.js-drawer-filter-close');
            if (closeBtn) closeBtn.click();
            // Scroll to top after filter is applied
            window.scrollTo({ top: 0, behavior: 'smooth' });
        });
    });

    sortSelects.forEach(sortSelect => {
        sortSelect.addEventListener('change', function(e) {
            const newValue = e.target.value;
            sortSelects.forEach(select => {
                if (select !== e.target) {
                    select.value = newValue;
                }
            });
            handleSortChange(newValue);
            // Scroll to top after sorting
            window.scrollTo({ top: 0, behavior: 'smooth' });
        });
    });

    // Mobile filter bar sticky positioning — keep below header
    const mobileFilterBar = document.querySelector('.js-mobile-filter-bar');
    if (mobileFilterBar) {
        const barRect = mobileFilterBar.getBoundingClientRect();
        const barOriginalTop = barRect.top + window.scrollY;

        const updateMobileBarTop = () => {
            const header = document.querySelector('.header');
            const headerMain = header ? header.querySelector('.header-main') : null;
            if (header && header.classList.contains('is-sticky') && headerMain) {
                mobileFilterBar.style.top = headerMain.offsetHeight + 'px';
            } else {
                mobileFilterBar.style.top = '0px';
            }

            // Toggle is-sticky class when bar is stuck
            const currentRect = mobileFilterBar.getBoundingClientRect();
            const stickyTop = parseFloat(mobileFilterBar.style.top) || 0;
            if (currentRect.top <= stickyTop + 1) {
                mobileFilterBar.classList.add('is-sticky');
            } else {
                mobileFilterBar.classList.remove('is-sticky');
            }
        };
        updateMobileBarTop();
        window.addEventListener('scroll', updateMobileBarTop, { passive: true });
        window.addEventListener('resize', updateMobileBarTop, { passive: true });
    }
});
